import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { StandbyService, StandbySlide } from '../../../_services/standby.service';

/**
 * Full-screen standby / screensaver overlay for customer-facing devices.
 *
 * Renders nothing until StandbyService reports active, so mounting it on a
 * device is inert while the feature is disabled. When active it cross-fades
 * through the merchant's slides and dismisses on any tap, forwarding that tap
 * to the service so the whole idle system re-arms.
 */
@Component({
  selector: 'app-standby-overlay',
  templateUrl: './standby-overlay.component.html',
  styleUrls: ['./standby-overlay.component.scss'],
})
export class StandbyOverlayComponent implements OnInit, OnDestroy {
  active = false;
  index = 0;
  slides: StandbySlide[] = [];

  private sub: Subscription;
  private cycleTimer: any = null;

  constructor(public standby: StandbyService, private zone: NgZone) {}

  ngOnInit(): void {
    this.sub = this.standby.active$.subscribe(active => {
      this.active = active;
      if (active) {
        this.slides = this.standby.slides || [];
        this.index = 0;
        this.startCycle();
      } else {
        this.stopCycle();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sub) { this.sub.unsubscribe(); }
    this.stopCycle();
  }

  get current(): StandbySlide | null {
    return this.slides[this.index] || null;
  }

  /** Any tap on the overlay wakes the device. */
  dismiss(): void {
    this.standby.notifyActivity();
  }

  trackByIndex(i: number): number {
    return i;
  }

  private startCycle(): void {
    this.stopCycle();
    if (this.slides.length <= 1) { return; } // single image: nothing to cycle
    const ms = Math.max(2, this.standby.slideDurationSeconds) * 1000;
    // Advance outside Angular so the interval doesn't hammer change detection;
    // re-enter only to bump the visible slide.
    this.zone.runOutsideAngular(() => {
      this.cycleTimer = setInterval(() => {
        this.zone.run(() => {
          this.index = (this.index + 1) % this.slides.length;
        });
      }, ms);
    });
  }

  private stopCycle(): void {
    if (this.cycleTimer) {
      clearInterval(this.cycleTimer);
      this.cycleTimer = null;
    }
  }
}
